import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import PayoutDetails from 'components/modules/accounting/payouts/payout-details';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@cargomatic/ui';

import Badge from 'components/v2/Badge';
import PayoutStatusBadge from 'components/v2/PayoutStatusBadge';
import { humanize } from 'underscore.string';
import CargomaticModal from 'components/v2/modal';
import { getPayoutsForShipment } from 'utils/AccountingWebAPIUtils';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import SessionStore from 'stores/SessionStore';
import { canShowApproveAction, canShowDeleteAction } from 'helpers/accounting';
import { useTranslation } from 'react-i18next';
import formatMoney from 'utils/formatters/Money';
import ExternalLink from 'components/v2/ExternalLink';
import { sluggify } from 'utils/localization/utils';
import AdjustmentTitle from './AdjustmentTitle';
import AdjustmentAction from './AdjustmentAction';
import DateField from './DateField';
import AddBaseCostForm from './AddBaseCostForm';
import { fetchDocuments, handleDownloadRateCard } from 'utils/documentUtils';
import { showToast } from 'stores/ToastStore';

export default function CarrierCharges({
  shipmentId,
  shipmentIsCurrent,
  charges,
  timezone,
  total,
  currency,
  isIntermodalShipmentType,
  onAddCustomAdjustment,
  onAddInternalAdjustment,
  onAddCustomerAdjustment,
  onAddBaseCost,
  onApproveBaseCost,
  onRemoveLineItem,
  carrierId,
  showDownloadRateCard,
}) {
  const { t } = useTranslation();

  const [lineItems, setLineItems] = useState([]);
  const [selectedLineItem, setSelectedLineItem] = useState();
  const [selectedBaseCost, setSelectedBaseCost] = useState();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showConfirmApprovalModal, setShowConfirmApprovalModal] =
    useState(false);
  const [showConfirmRemovalModal, setShowConfirmRemovalModal] = useState(false);
  const [recentRateCard, setRecentRateCard] = useState({});

  function handleHideDetailsModal() {
    setShowDetailsModal(false);
  }

  function handleApprove(lineItem) {
    return function () {
      setShowConfirmApprovalModal(true);
      setSelectedBaseCost(lineItem);
    };
  }

  function handleRemove(lineItem) {
    return function () {
      setShowConfirmRemovalModal(true);
      setSelectedLineItem(lineItem);
    };
  }

  function handleHideConfirmApprovalModal() {
    setShowConfirmApprovalModal(false);
    setSelectedLineItem(null);
    getPayouts();
  }

  function handleConfirmApproveBaseCost() {
    onApproveBaseCost(selectedBaseCost);
    handleHideConfirmApprovalModal();
  }

  async function handleConfirmLineItemRemoval() {
    await onRemoveLineItem(selectedLineItem, 'payables');
    handleHideConfirmRemovalModal();
  }

  function handleHideConfirmRemovalModal() {
    setShowConfirmRemovalModal(false);
    setSelectedLineItem(null);
  }

  async function getPayouts() {
    try {
      const {
        entity: { payouts },
      } = await getPayoutsForShipment(shipmentId);

      const mappedLineItems = charges.map((lineItem) => {
        const isFirstFuelSurchargeItem =
          charges.find((c) => c.charge_code === 'FSC')?._id === lineItem._id;
        const payout = payouts.find((p) => p._id === lineItem.payout_id);
        if (!payout) {
          return {
            ...lineItem,
            payout: null,
            isFirstFuelSurchargeItem,
          };
        }
        const { _id, number, timestamp, status, paid_date } = payout;
        return {
          ...lineItem,
          payout: {
            _id,
            number,
            timestamp,
            status,
            paid_date,
          },
          isFirstFuelSurchargeItem,
        };
      });

      /*
       * This section of code sorts the line item charges for easy
       * identification of quick pay line item, starting with the parent line
       * item, followed by relevant quick pay discounts, and so on.
       * This improves readabilty for the admin user
       */

      const parentCharges = mappedLineItems.filter(
        (item) => !item.parent_adjustment_id,
      );
      const childrenCharges = mappedLineItems.filter(
        (item) => item.parent_adjustment_id,
      );

      const childrenMap = childrenCharges.reduce((map, child) => {
        const parentId = child.parent_adjustment_id;
        if (!map[parentId]) {
          map[parentId] = [];
        }
        map[parentId].push(child);
        return map;
      }, {});

      const arrangeLineItems = [];
      parentCharges.forEach((parent) => {
        arrangeLineItems.push(parent);

        // Check for children with `_id` as parent reference.
        if (childrenMap[parent._id]) {
          arrangeLineItems.push(...childrenMap[parent._id]);
        }

        // Check for children with `accessorial_request_id` as parent reference.
        if (
          parent.accessorial_request_id &&
          childrenMap[parent.accessorial_request_id]
        ) {
          arrangeLineItems.push(...childrenMap[parent.accessorial_request_id]);
        }
      });

      setLineItems(arrangeLineItems);
    } catch (e) {
      console.log('Error getting payouts', e);
    }
  }

  function renderPayoutLink(lineItem) {
    if (!lineItem.payout) {
      return null;
    }
    const {
      payout: { _id, number },
    } = lineItem;

    return (
      <ExternalLink
        dataTestId="ExternalLink-8f6da654"
        href={`/payouts/${_id}`}
        title={number}
        showIcon
      />
    );
  }

  const isFinance = SessionStore.isFinance() || SessionStore.isFinanceAdmin();

  function showApproveButton(lineItem) {
    return isIntermodalShipmentType && canShowApproveAction(lineItem);
  }

  function showRemoveButton(lineItem) {
    return canShowDeleteAction(lineItem, isFinance, isIntermodalShipmentType);
  }

  useEffect(() => {
    getPayouts();
    if (carrierId) getRateCard();
  }, [charges]);

  const renderDownloadLink = () => {
    if (recentRateCard?.pdfUrl) {
      return (
        <a
          onClick={() => handleDownloadRateCard(recentRateCard?.pdfUrl, true)}
          className="underline"
        >
          {t('LATEST_CARRIER_RATE_CARD')}
        </a>
      );
    }
    return <p className="text-red">{t('NO_RATE_CARD_GENERATED')}</p>;
  };

  async function getRateCard() {
    try {
      const rateCardData = await fetchDocuments(carrierId, 'carrier_rate_card');
      setRecentRateCard(rateCardData);
    } catch (err) {
      console.error(err);
      showToast('', t('FAILED_TO_DOWNLOAD_RATE_CARD'), 'error');
    }
  }

  // Bens recommendation for accessorials beyond the 2 week period.
  // Some might be incorrectly marked but it was a trade off decision
  function getRateSource(lineItem) {
    if (!lineItem?.rate_source) {
      return lineItem?.autorated ? t('AUTOMATED') : t('MANUAL');
    }

    return t(sluggify(lineItem.rate_source));
  }

  function setChargeStatus(lineItem) {
    if (lineItem?.payout) {
      return <PayoutStatusBadge status={lineItem.payout?.status} />;
    }

    if (lineItem?.has_pending_state) {
      return (
        <Badge dataTestId="Badge-d114a320" color="gray">
          {humanize(t('PENDING'))}
        </Badge>
      );
    }

    return null;
  }

  return (
    <div data-testid="div-d35e450b">
      <div className="flex items-center gap-4 mb-4">
        <h4 data-testid="h3-f3726b77" className="text-base font-semibold">
          {t('CARRIER_CHARGES')}
        </h4>
        {showDownloadRateCard && renderDownloadLink()}
      </div>
      <div data-testid="div-763fa60e" className="overflow-x-auto">
        <Table data-testid="table-3bab90ce" className="">
          <TableHeader data-testid="thead-24d618d2">
            <TableRow data-testid="tr-d37e1215">
              <TableHead data-testid="th-b8048929" className="w-48">
                {t('CHARGE')}
              </TableHead>
              <TableHead data-testid="th-f0ade268" className="w-20">
                {t('COST')}
              </TableHead>
              <TableHead data-testid="th-314678fe" className="w-40">
                {t('ENTERED_DATE')}
              </TableHead>
              <TableHead data-testid="th-21a3223c" className="w-40">
                {t('RATE_SOURCE')}
              </TableHead>
              <TableHead data-testid="th-b02300c0" className="w-52">
                {t('APPROVED_PAYOUT')}
              </TableHead>
              <TableHead data-testid="th-aca19183" className="w-40">
                {t('APPROVED_PAYOUT_DATE')}
              </TableHead>
              <TableHead data-testid="th-2f1e7db9" className="w-24">
                {t('PAYOUT_STATUS')}
              </TableHead>
              <TableHead data-testid="th-b73bb107" className="w-32">
                {t('PAID_DATE')}
              </TableHead>
              <TableHead data-testid="th-b2590f55">&nbsp;</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="tbody-c5ceb454">
            {lineItems.map((lineItem, index) => (
              <TableRow data-testid={`tr-75a4ad3d-${index}`} key={lineItem._id}>
                <TableCell
                  data-testid={`td-fcb8f8ec-${index}`}
                  className="break-words"
                >
                  <AdjustmentTitle
                    lineItem={lineItem}
                    unapplied={isIntermodalShipmentType && !lineItem.equipment}
                  />
                </TableCell>
                <TableCell data-testid={`td-042de8b8-${index}`}>
                  {formatMoney(lineItem.value?.toFixed(2), lineItem?.currency)}
                </TableCell>
                <TableCell data-testid={`td-2b5a7029-${index}`}>
                  <DateField date={lineItem.timestamp} timezone={timezone} />
                </TableCell>
                <TableCell
                  data-testid={`td-d4fdca1f-${index}`}
                  className="text-capitalize"
                >
                  {getRateSource(lineItem)}
                </TableCell>
                <TableCell data-testid={`td-50403cb8-${index}`}>
                  {renderPayoutLink(lineItem)}
                </TableCell>
                <TableCell data-testid={`td-ed299596-${index}`}>
                  <DateField
                    date={lineItem.payout?.timestamp}
                    timezone={timezone}
                  />
                </TableCell>
                <TableCell data-testid={`td-da5052a8-${index}`}>
                  {setChargeStatus(lineItem)}
                </TableCell>
                <TableCell data-testid={`td-89c7346e-${index}`}>
                  <DateField
                    date={lineItem.payout?.paid_date}
                    timezone={timezone}
                    showTime={false}
                  />
                </TableCell>
                <TableCell data-testid={`td-4a199dc5-${index}`}>
                  <AdjustmentAction
                    lineItem={lineItem}
                    shipmentIsCurrent={shipmentIsCurrent}
                    isIntermodalShipmentType={isIntermodalShipmentType}
                    onAddCustomAdjustment={onAddCustomAdjustment}
                    onAddCustomerAdjustment={onAddCustomerAdjustment}
                    onAddInternalAdjustment={onAddInternalAdjustment}
                    showApprove={showApproveButton(lineItem)}
                    onApprove={handleApprove(lineItem)}
                    showRemove={showRemoveButton(lineItem)}
                    onRemove={handleRemove(lineItem)}
                  />
                </TableCell>
              </TableRow>
            ))}
            <TableRow data-testid="tr-2145a9b9">
              <TableCell data-testid="td-dec834fa">
                <div data-testid="div-0cffa7dc" className="w-48">
                  {t('TOTAL')}
                </div>
              </TableCell>
              <TableCell data-testid="td-72d7df69" className="font-bold">
                {formatMoney(total, lineItems[0]?.currency)}
              </TableCell>
              <TableCell data-testid="td-07c4d5f1" colSpan={7}>
                &nbsp;
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <AddBaseCostForm
          ledgerType="payables"
          ledgers={charges}
          onAddBaseCost={onAddBaseCost}
          currency={currency}
        />
      </div>
      {showDetailsModal && (
        <CargomaticModal
          dataTestId="CargomaticModal-8ae21013"
          title=""
          show={showDetailsModal}
          footer={null}
          body={<PayoutDetails payoutId={selectedLineItem} />}
          onHide={handleHideDetailsModal}
          dialogClassName="modal-override"
          location="top"
        />
      )}
      {showConfirmApprovalModal && (
        <ConfirmModal
          dataTestId="ConfirmModal-1e376964"
          show={showConfirmApprovalModal}
          onConfirm={handleConfirmApproveBaseCost}
          onHide={handleHideConfirmApprovalModal}
          title={t('APPROVE_CARRIER_COST_TITLE')}
          message={t('APPROVE_CARRIER_COST_MESSAGE')}
          affirmative={t('APPROVE')}
          hideButtonText={t('NO')}
        />
      )}
      {showConfirmRemovalModal && (
        <ConfirmModal
          dataTestId="ConfirmModal-2fbe9ab9"
          show={showConfirmRemovalModal}
          onConfirm={handleConfirmLineItemRemoval}
          onHide={handleHideConfirmRemovalModal}
          title={t('APPROVE_ITEM_REMOVAL_TITLE')}
          message={t('APPROVE_ITEM_REMOVAL_MESSAGE')}
          affirmative={t('APPROVE')}
          hideButtonText={t('NO')}
        />
      )}
    </div>
  );
}

CarrierCharges.propTypes = {
  timezone: PropTypes.string,
};

CarrierCharges.defaultProps = {
  timezone: 'America/Los_Angeles',
};
